/**
 * O início do mapa, sem React e sem rede: trocar o que faz a automação começar
 * e montar a agenda da rotina.
 *
 * O início é o gatilho: um evento do CRM ("Chegou uma mensagem") ou uma agenda
 * ("Todo dia às 8h"). Trocar de evento apaga o "Só se", porque a regra lê os
 * campos do evento de antes e não valeria no novo.
 */

import type { BlocosDoEditor } from '@cliqcrm/contracts';
import type { Configuracao } from './edicao';

type Gatilho = Configuracao['gatilho'];
type Agenda = Extract<Gatilho, { tipo: 'agenda' }>;
type Horario = Agenda['horarios'][number];

export const FUSO_PADRAO = 'America/Sao_Paulo';

/** Os dias da semana como o serviço os conta: 0 é domingo. */
export const DIAS_DA_SEMANA: readonly { dia: number; curto: string; nome: string }[] = [
  { dia: 1, curto: 'Seg', nome: 'segunda' },
  { dia: 2, curto: 'Ter', nome: 'terça' },
  { dia: 3, curto: 'Qua', nome: 'quarta' },
  { dia: 4, curto: 'Qui', nome: 'quinta' },
  { dia: 5, curto: 'Sex', nome: 'sexta' },
  { dia: 6, curto: 'Sáb', nome: 'sábado' },
  { dia: 0, curto: 'Dom', nome: 'domingo' },
];

export const FUSOS: readonly { valor: string; rotulo: string }[] = [
  { valor: 'America/Sao_Paulo', rotulo: 'Brasília' },
  { valor: 'America/Manaus', rotulo: 'Manaus' },
  { valor: 'America/Cuiaba', rotulo: 'Cuiabá' },
  { valor: 'America/Rio_Branco', rotulo: 'Rio Branco' },
  { valor: 'America/Noronha', rotulo: 'Fernando de Noronha' },
];

/** A rotina com que a agenda nasce: todo dia, às 8h, no horário de Brasília. */
export function agendaInicial(): Agenda {
  return {
    tipo: 'agenda',
    fuso: FUSO_PADRAO,
    horarios: [{ dia_da_semana: null, hora: 8, minuto: 0 }],
  };
}

/** Um dos inícios que a paleta oferece: cada evento do CRM e a rotina agendada. */
export interface InicioDaPaleta {
  readonly chave: string;
  readonly titulo: string;
  readonly descricao: string;
  readonly gatilho: Gatilho;
}

export function iniciosDaPaleta(blocos: BlocosDoEditor | undefined): InicioDaPaleta[] {
  const eventos = (blocos?.eventos ?? [])
    .filter((e) => !e.em_breve)
    .map((e) => ({
      chave: `evento:${e.evento}`,
      titulo: e.titulo,
      descricao: 'Quando isso acontece no CRM.',
      gatilho: { tipo: 'evento', evento: e.evento } as Gatilho,
    }));
  return [
    ...eventos,
    {
      chave: 'agenda',
      titulo: 'Rotina agendada',
      descricao: 'Num horário fixo, como todo dia às 8h.',
      gatilho: agendaInicial(),
    },
  ];
}

/** Este início é o que o mapa já tem? A agenda conta como uma só, qualquer que seja. */
export function ehOInicioAtual(atual: Gatilho, inicio: InicioDaPaleta): boolean {
  if (inicio.gatilho.tipo === 'agenda') return atual.tipo === 'agenda';
  return (
    atual.tipo === 'evento' &&
    inicio.gatilho.tipo === 'evento' &&
    atual.evento === inicio.gatilho.evento
  );
}

/**
 * O mapa com o início trocado. O mesmo início não muda nada (nem a agenda já
 * montada); outro apaga o "Só se", que lia os campos do início de antes.
 */
export function trocarInicio(config: Configuracao, inicio: InicioDaPaleta): Configuracao {
  if (ehOInicioAtual(config.gatilho, inicio)) return config;
  return { ...config, gatilho: inicio.gatilho, condicao: null };
}

// ── A agenda, como a pessoa a escolhe ────────────────────────────────────

export interface FormularioDaAgenda {
  /** `null`: todo dia. Senão, os dias escolhidos (0 é domingo). */
  readonly dias: readonly number[] | null;
  /** Os horários, como `HH:MM`, sem repetir. */
  readonly horas: readonly string[];
  readonly fuso: string;
}

const doisDigitos = (n: number): string => String(n).padStart(2, '0');

export function formularioDaAgenda(agenda: Agenda): FormularioDaAgenda {
  const todoDia = agenda.horarios.some((h) => h.dia_da_semana === null);
  const dias = todoDia
    ? null
    : [...new Set(agenda.horarios.map((h) => h.dia_da_semana as number))].sort();
  const horas = [
    ...new Set(agenda.horarios.map((h) => `${doisDigitos(h.hora)}:${doisDigitos(h.minuto)}`)),
  ].sort();
  return { dias, horas, fuso: agenda.fuso };
}

/**
 * A agenda do formulário: cada dia escolhido em cada horário (todo dia é o dia
 * `null`). Horário que não é `HH:MM` fica de fora, e o problema diz por quê.
 */
export function agendaDoFormulario(f: FormularioDaAgenda): Agenda {
  const horas = f.horas
    .map((h) => /^([01]\d|2[0-3]):([0-5]\d)$/.exec(h))
    .filter((m): m is RegExpExecArray => m !== null)
    .map((m) => ({ hora: Number(m[1]), minuto: Number(m[2]) }));
  const dias: (number | null)[] = f.dias === null ? [null] : [...f.dias];
  const horarios: Horario[] = dias.flatMap((dia_da_semana) =>
    horas.map((h) => ({ dia_da_semana, ...h })),
  );
  return { tipo: 'agenda', fuso: f.fuso, horarios };
}

export function problemasDaAgenda(f: FormularioDaAgenda): string[] {
  const p: string[] = [];
  if (f.dias !== null && f.dias.length === 0) p.push('Escolha pelo menos um dia.');
  if (f.horas.length === 0) p.push('Diga pelo menos um horário.');
  if (f.horas.some((h) => !/^([01]\d|2[0-3]):([0-5]\d)$/.test(h))) {
    p.push('Use o horário no formato 08:00.');
  }
  if ((f.dias?.length ?? 1) * f.horas.length > 50) p.push('São no máximo 50 horários na semana.');
  return p;
}

/** A agenda numa frase: "Todo dia às 08:00" ou "Seg, Qua às 09:00 e 14:00". */
export function resumoDaAgenda(agenda: Agenda): string {
  const f = formularioDaAgenda(agenda);
  const dias =
    f.dias === null
      ? 'Todo dia'
      : f.dias.length === 5 && [1, 2, 3, 4, 5].every((d) => f.dias!.includes(d))
        ? 'De segunda a sexta'
        : DIAS_DA_SEMANA.filter((d) => f.dias!.includes(d.dia))
            .map((d) => d.curto)
            .join(', ');
  const horas =
    f.horas.length <= 1
      ? f.horas.join('')
      : `${f.horas.slice(0, -1).join(', ')} e ${f.horas.at(-1)}`;
  return horas ? `${dias} às ${horas}` : dias;
}
